import { action, autorun } from "mobx";
import { ItemDetailPageModel } from "./ItemDetailPageModels";
import {
  InventorySettings,
  ProductAttributeInputModel,
  ProductAttributeMappingsInput,
  ProductAttributesModel,
  ReOrderSttingsRequest,
} from "./ProductModel";
import { MediaModel } from "./MediaModel";
import {
  IAPIGetEditProductModel,
  IAPIPriceModel,
  IAPIProductAdvancedModel,
  IAPIUpdateVariantModel,
} from "../interfaces";
import {
  CoreInputModelUtil,
  CoreModel,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { PricingPageInputModel } from "./PriceInputs.model";
import { RelatedSectionModel } from "./DonationAndItemModel";

export class ProductVariantEditableModel {
  price?: ProductVariantEditablePriceModel;
  attribute?: ProductAttributeMappingsInput;
  advanced: ProductVariantEditableAdvancedModel;
  medias: MediaModel[];
  attributeIndex: number;
  constructor(data?: Partial<ProductVariantEditableModel>) {
    this.price = new ProductVariantEditablePriceModel(data?.price);
    this.attribute =
      data?.attribute && new ProductAttributeMappingsInput(data.attribute);
    this.advanced = new ProductVariantEditableAdvancedModel(data?.advanced);
    this.attributeIndex = data?.attributeIndex || 0;
    this.medias = data?.medias?.map((media) => new MediaModel(media)) || [];
  }

  static init(item: ProductItemModel) {
    const clonedAdvanceddata = new ProductVariantEditableAdvancedModel({
      ...item.advancedSection,
    });
    if (item.detailSection.hasVariants) {
      if (item.variants?.showOnHand) {
        const onHandQuantity =
          item.selectedVariant?.onHand || item.onHandQuantity;
        clonedAdvanceddata.onHandQuantity = onHandQuantity.toString();
      }
    }
    const mappedData: Partial<ProductVariantEditableModel> = {
      price:
        item.selectedVariant &&
        new ProductVariantEditablePriceModel({
          price: item.selectedVariant.price,
          originalPrice: item.selectedVariant.originalPrice,
          cost: item.selectedVariant.cost,
          priceByVariant: item.variants?.priceByVariant,
          originalPriceByVariant: item.variants?.originalPriceByVariant,
          costByVariant: item.variants?.costByVariant,
        }),
      attribute:
        item.variants?.options[item.selectedVariantIndex] &&
        new ProductAttributeMappingsInput(
          item.variants?.options[item.selectedVariantIndex]
        ),
      advanced: clonedAdvanceddata,
      attributeIndex: item.selectedVariantIndex,
    };
    return new ProductVariantEditableModel(mappedData);
  }

  validate(isAutoRun = false): boolean {
    const isValidPrice = this.price ? this.price?.validate(isAutoRun) : true;
    let isValidVariant = true;
    if (this.attribute) {
      this.attribute.attributeMappings.forEach((attr) => {
        if (!attr.validate(isAutoRun)) {
          isValidVariant = false;
        }
      });
    }

    const isAdvancedValid = this.advanced.validate(isAutoRun);
    return isValidPrice && isValidVariant && isAdvancedValid;
  }

  createAPIModel(): IAPIUpdateVariantModel {
    return {
      price: this.price?.createAPIModel() as IAPIPriceModel,
      advanced: this.advanced.createAPIModel(),
      attributeMappings:
        this.attribute?.attributeMappings.map((attr) =>
          attr.createAPIModel()
        ) || [],
      attributeIndex: this.attributeIndex,
      productId: "",
      accountId: "",
      medias: this.medias || [],
    };
  }
}

export class ProductVariantEditablePriceModel extends CoreModel {
  price: string;
  originalPrice: string;
  cost: string;
  priceByVariant: boolean;
  originalPriceByVariant: boolean;
  costByVariant: boolean;
  errors: InputErrorModel<ProductVariantEditablePriceModel>[];
  constructor(data?: Partial<ProductVariantEditablePriceModel>) {
    super();
    this.price = data?.price || "";
    this.originalPrice = data?.originalPrice || "";
    this.cost = data?.cost || "";
    this.priceByVariant = data?.priceByVariant || false;
    this.originalPriceByVariant = data?.originalPriceByVariant || false;
    this.costByVariant = data?.costByVariant || false;
    this.errors = data?.errors || [];
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof ProductVariantEditablePriceModel): boolean {
    return this.errors.some((error) => error.field === key);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ProductVariantEditablePriceModel>[] = [];
    if (this.priceByVariant) {
      this.validateDecimalValue(this.price, "price", newErrors);
    }
    if (this.costByVariant) {
      this.validateDecimalValue(this.cost, "cost", newErrors);
    }

    if (this.originalPriceByVariant) {
      this.validateDecimalValue(this.originalPrice, "originalPrice", newErrors);
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return newErrors.length === 0;
  }

  createAPIModel(): IAPIPriceModel {
    return {
      price: parseFloat(this.price) || 0,
      originalPrice: parseFloat(this.originalPrice) || 0,
      cost: parseFloat(this.cost) || 0,
      priceByVariant: this.priceByVariant,
      originalPriceByVariant: this.originalPriceByVariant,
      costByVariant: this.costByVariant,
    };
  }
}

export class ProductVariantEditableAdvancedModel extends CoreModel {
  tax: boolean;
  surcharge: boolean;
  discount: boolean;
  review: boolean;
  return: boolean;
  sku: string;
  barCode: string;
  onHandQuantity: string;
  isSellOnOutOfStock: boolean;
  isPromote: boolean;
  isLimitPurchaseQuantity: boolean;
  quantityLimit: string;
  returnDays: string;
  isTrackInventory: boolean;
  errors: InputErrorModel<ProductVariantEditableAdvancedModel>[];
  constructor(data?: Partial<ProductVariantEditableAdvancedModel>) {
    super();
    this.tax = data?.tax || false;
    this.surcharge = data?.surcharge || false;
    this.discount = data?.discount || false;
    this.review = data?.review || false;
    this.return = data?.return || false;
    this.sku = data?.sku || "";
    this.barCode = data?.barCode || "";
    this.onHandQuantity = data?.onHandQuantity || "";
    this.isSellOnOutOfStock = data?.isSellOnOutOfStock || false;
    this.isLimitPurchaseQuantity = data?.isLimitPurchaseQuantity || false;
    this.quantityLimit = data?.quantityLimit || "";
    this.isPromote = data?.isPromote || false;
    this.errors = data?.errors || [];
    this.returnDays = data?.returnDays || "";
    this.isTrackInventory = data?.isTrackInventory || false;
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(key: keyof ProductVariantEditableAdvancedModel): boolean {
    return this.errors.some((error) => error.field === key);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ProductVariantEditableAdvancedModel>[] =
      [];
    if (this.isTrackInventory) {
      if (this.isLimitPurchaseQuantity) {
        this.validateDecimalValue(
          this.quantityLimit,
          "quantityLimit",
          newErrors
        );
      }

      if (
        CoreInputModelUtil.validateDecimalValue(
          this.onHandQuantity,
          "onHandQuantity",
          newErrors,
          undefined,
          false
        )
      ) {
        this.validateNumber(+this.onHandQuantity, "onHandQuantity", newErrors);
      }
    }

    if (this.return) {
      CoreInputModelUtil.validateDecimalValue(
        this.returnDays,
        "returnDays",
        newErrors,
        undefined,
        true
      );
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return newErrors.length === 0;
  }

  createAPIModel(): IAPIProductAdvancedModel {
    return {
      tax: this.tax,
      surcharge: this.surcharge,
      discount: this.discount,
      review: this.review,
      return: this.return,
      sku: this.sku,
      barCode: this.barCode,
      onHandQuantity: this.onHandQuantity,
      isSellOnOutOfStock: this.isSellOnOutOfStock,
      isPromote: this.isPromote,
      isLimitPurchaseQuantity: this.isLimitPurchaseQuantity,
      quantityLimit: parseInt(this.quantityLimit),
      returnDays: parseInt(this.returnDays) || 0,
    };
  }

  static deserializeAPIModel(data: IAPIGetEditProductModel) {
    const mappedData: Partial<ProductVariantEditableAdvancedModel> = {
      tax: data.tax,
      surcharge: data.surcharge,
      discount: data.discount,
      review: data.review,
      return: data.return,
      sku: data.sku,
      barCode: data.barCode,
      onHandQuantity: data.inventorySettings?.available?.toString() || "",
      isSellOnOutOfStock: data.inventorySettings?.isSellOnOutOfStock || false,
      isPromote: data.inventorySettings?.isPromote || false,
      isLimitPurchaseQuantity:
        data.inventorySettings?.isLimitPurchaseQuantity || false,
      quantityLimit: data.inventorySettings?.quantityLimit?.toString() || "",
      returnDays: data.returnDays?.toString() || "",
    };
    return new ProductVariantEditableAdvancedModel(mappedData);
  }
}

export class ProductItemModel {
  detailSection: ItemDetailPageModel;
  advancedSection: ProductVariantEditableAdvancedModel;
  pricingInputs: PricingPageInputModel | null = null;
  selectedAttributes: ProductAttributesModel[];
  inventorySettings?: InventorySettings;
  reOrderSettings?: ReOrderSttingsRequest;
  allowReOrder: boolean;
  isTrackInventory: boolean;
  onHandQuantity: number;
  variants?: ProductAttributeInputModel | null;
  selectedVariant?: ProductAttributeMappingsInput;
  isEditVariant: boolean;
  selectedVariantIndex: number;
  editableVariant?: ProductVariantEditableModel;
  relatedSectionData?: RelatedSectionModel;
  showLibrary: boolean;
  showUpload: boolean;
  constructor(data?: Partial<ProductItemModel>) {
    this.detailSection =
      (data && data.detailSection) || new ItemDetailPageModel();
    this.selectedAttributes = (data && data.selectedAttributes) || [];
    this.inventorySettings = data && data.inventorySettings;
    this.reOrderSettings = data && data.reOrderSettings;
    this.allowReOrder = (data && data.allowReOrder) || false;
    this.isTrackInventory = data?.isTrackInventory === true;
    this.onHandQuantity = data?.onHandQuantity || 0;
    this.variants =
      data?.variants && new ProductAttributeInputModel(data.variants);
    this.selectedVariant =
      data?.selectedVariant &&
      new ProductAttributeMappingsInput(data.selectedVariant);
    this.isEditVariant = data?.isEditVariant === true;
    this.selectedVariantIndex = data?.selectedVariantIndex || 0;
    this.editableVariant =
      data?.editableVariant &&
      new ProductVariantEditableModel(data.editableVariant);
    this.advancedSection =
      data?.advancedSection || new ProductVariantEditableAdvancedModel();
    this.pricingInputs = data?.pricingInputs || null;
    this.relatedSectionData =
      data?.relatedSectionData &&
      new RelatedSectionModel(data?.relatedSectionData);
    this.showLibrary = data?.showLibrary || false;
    this.showUpload = data?.showUpload || false;
  }

  static deserializeAPIModel(data: IAPIGetEditProductModel): ProductItemModel {
    const defaultPrice = data.prices?.find(
      (price) => price.id === data.defaultPriceId
    );
    const mappedData: Partial<ProductItemModel> = {
      detailSection: ItemDetailPageModel.deserializeAPIModel(data),
      selectedAttributes:
        data.selectedAttributes?.map((attr) =>
          ProductAttributesModel.deserializeAPIModel(attr)
        ) || [],
      inventorySettings: data.inventorySettings
        ? InventorySettings.deserializeAPIModel(
            data.inventorySettings,
            data.hasVariants
          )
        : undefined,
      allowReOrder: data.allowReOrder || false,
      reOrderSettings:
        data.allowReOrder && data.reOrderSettings
          ? ReOrderSttingsRequest.deserializeAPIModel(data.reOrderSettings)
          : undefined,
      isTrackInventory: data.isTrackInventory,
      onHandQuantity: data.inventorySettings?.available || 0,
      variants: data.hasVariants
        ? data.variants &&
          ProductAttributeInputModel.deserializeAPI(
            data.variants,
            data.isTrackInventory
          )
        : null,
      advancedSection:
        ProductVariantEditableAdvancedModel.deserializeAPIModel(data),
      pricingInputs: defaultPrice
        ? PricingPageInputModel.deserializeAPIModel(defaultPrice)
        : null,
      relatedSectionData:
        data.options?.relatedItems &&
        RelatedSectionModel.deserializeAPIModel(data.options?.relatedItems),
    };
    return new ProductItemModel(mappedData);
  }

  @action
  toggleShowLibrary(show: boolean, fromUpload: boolean = false) {
    this.showLibrary = show;
    this.showUpload = fromUpload;
  }
}
